import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSessionStore } from "@/stores/sessionStore";
import { useSessionQuery } from "@/features/auth/hooks";
import type { ApiScope } from "@/lib/apiClient";
import type { UserRole } from "@/types/api";
import { homeForRole } from "./navigation";

/**
 * Guards de rota por papel (Fase 0).
 *
 * Isto e navegacao, nao seguranca. Quem autoriza de verdade e o backend, em
 * toda requisicao — um guard de frontend so evita que a pessoa veja uma tela
 * vazia ou quebrada. Nunca renderize dado sensivel confiando so num guard.
 */

function FullPageLoader({ label = "Carregando..." }: { label?: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-background" role="status" aria-live="polite">
      <div className="flex flex-col items-center gap-3">
        <div className="size-8 animate-spin rounded-full border-2 border-primary-200 border-t-primary-600" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

interface RequireAuthProps {
  children: ReactNode;
  /** Papeis aceitos. Vazio = qualquer usuario autenticado no escopo. */
  roles?: Array<UserRole | "operator">;
  scope?: ApiScope;
  redirectTo: string;
}

export function RequireAuth({ children, roles, scope = "user", redirectTo }: RequireAuthProps) {
  const location = useLocation();
  const status = useSessionStore((s) => s.status);
  const user = useSessionStore((s) => s.user);
  const operator = useSessionStore((s) => s.operator);

  // Confirma a sessao no servidor. Enquanto o status for "unknown" nao da
  // para decidir: mandar ao login agora faria o app piscar a tela de entrada
  // a cada F5 mesmo com o cookie valido.
  const { isLoading } = useSessionQuery(scope);

  if (status === "unknown" && isLoading) return <FullPageLoader />;

  const actor = scope === "operator" ? operator : user;

  if (!actor) {
    // `state.from` permite voltar para onde a pessoa queria ir depois do
    // login, em vez de sempre cair na home.
    return <Navigate to={redirectTo} replace state={{ from: location.pathname + location.search }} />;
  }

  if (roles && roles.length > 0) {
    const actorRole = scope === "operator" ? "operator" : user?.role;

    if (!actorRole || !roles.includes(actorRole)) {
      // Manda para a area correta do papel, e nao para o login: a pessoa
      // esta autenticada, so entrou na porta errada.
      return <Navigate to={homeForRole(actorRole)} replace />;
    }
  }

  return <>{children}</>;
}

/** Impede que alguem ja logado fique na tela de login. */
export function RedirectIfAuthenticated({
  children,
  scope = "user",
}: {
  children: ReactNode;
  scope?: ApiScope;
}) {
  const location = useLocation();
  const user = useSessionStore((s) => s.user);
  const operator = useSessionStore((s) => s.operator);
  const actor = scope === "operator" ? operator : user;

  if (actor) {
    const from = (location.state as { from?: string } | null)?.from;
    const fallback = scope === "operator" ? "/admin" : homeForRole(user?.role ?? null);
    return <Navigate to={from ?? fallback} replace />;
  }

  return <>{children}</>;
}

